import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { store } from 'components/App';
import {
  getMfaQrCodeRequest,
  setupPhoneNumberRequest,
} from 'modules/auth/components/MfaPanel/actions';
import { cleanupSmsInfo, sendSmsVerificationRequest } from '../../../actions';
import { change, clearFields, getFormValues, reset, setSubmitSucceeded } from 'redux-form';
import { MFA_SETUP_VERIFICATION_FORM } from 'modules/auth/components/MfaPanel/constants';
import { getSecret, getQrCodeImage } from 'modules/auth/components/MfaPanel/selectors';
import { createPendingSelector } from 'modules/api/selectors';
import saga from '../sagas';
import MfaSetup from '../../../components/MfaSetup';
import { MFA_TYPE, SMS_VERIFICATION_TYPE } from '../../../constants';
import { getMfaSettings, getSmsSentAt, getUserRole } from 'modules/auth/selectors';
import isSmsCodeValid from 'modules/auth/helpers/isSmsCodeValid';
import { USER_ROLE } from '../../../../../constants/userRoles';
import { authSetupMfaRequest } from '../actions';

const MfaSetupContainer = () => {
  const dispatch = useDispatch();
  const qrCode = useSelector(getQrCodeImage);
  const secret = useSelector(getSecret);
  const setupPending = useSelector(createPendingSelector(authSetupMfaRequest));
  const loading = useSelector(createPendingSelector(getMfaQrCodeRequest));
  const { type } = useSelector(getFormValues(MFA_SETUP_VERIFICATION_FORM)) || {};
  const { phoneNumber, allowedTypes, required } = useSelector(getMfaSettings);
  const role = useSelector(getUserRole);
  const setupPhoneNumberPending = useSelector(createPendingSelector(setupPhoneNumberRequest));
  const sendSMSPending = useSelector(createPendingSelector(sendSmsVerificationRequest));
  const smsSentAt = useSelector((state) => getSmsSentAt(state, SMS_VERIFICATION_TYPE.MFA));
  const mfaTypeRequired = allowedTypes?.length > 1 && !type;
  const sendSmsRequired = !!(phoneNumber && !isSmsCodeValid(smsSentAt));
  const setupPhoneRequired = type === MFA_TYPE.SMS && !phoneNumber;
  const canBack = allowedTypes?.length > 1;
  const initialValues = useMemo(
    () => ({
      type: allowedTypes?.length === 1 ? allowedTypes[0] : '',
    }),
    [allowedTypes],
  );

  useEffect(() => {
    store.injectSaga('mfaSetupSaga', saga);

    return () => {
      store.injectSaga('mfaSetupSaga', null);
    };
  }, []);

  useEffect(() => {
    dispatch(getMfaQrCodeRequest());

    return () => {
      dispatch(reset(MFA_SETUP_VERIFICATION_FORM));
      dispatch(cleanupSmsInfo(SMS_VERIFICATION_TYPE.MFA));
    };
  }, [dispatch]);

  const handleSubmit = useCallback(
    ({ code, type, phoneInfo }) => {
      if (type === MFA_TYPE.SMS) {
        if (setupPhoneRequired) {
          dispatch(setupPhoneNumberRequest(phoneInfo));
        } else if (sendSmsRequired) {
          dispatch(change(MFA_SETUP_VERIFICATION_FORM, 'code', ''));
          dispatch(sendSmsVerificationRequest());
        } else {
          dispatch(authSetupMfaRequest({ code, type }));
        }
      } else {
        dispatch(authSetupMfaRequest({ code, secret, type }));
      }
    },
    [dispatch, secret, setupPhoneRequired, sendSmsRequired],
  );

  const handleResetMfa = useCallback(
    (e) => {
      e.preventDefault();
      dispatch(reset(MFA_SETUP_VERIFICATION_FORM));
    },
    [dispatch],
  );

  const handleCodeExpired = useCallback(() => {
    dispatch(change(MFA_SETUP_VERIFICATION_FORM, 'code', ''));
    dispatch(change(MFA_SETUP_VERIFICATION_FORM, 'phoneInfo', null));
    dispatch(cleanupSmsInfo(SMS_VERIFICATION_TYPE.MFA));
  }, [dispatch]);

  const handleCodeSent = useCallback(() => {
    dispatch(clearFields(MFA_SETUP_VERIFICATION_FORM, false, false, 'code'));
    //hack to reset sumbitFailed flag to do not show errors (e.g. required).
    dispatch(setSubmitSucceeded(MFA_SETUP_VERIFICATION_FORM));
  }, [dispatch]);

  return (
    <MfaSetup
      required={required || role !== USER_ROLE.CLIENT}
      form={MFA_SETUP_VERIFICATION_FORM}
      loading={loading}
      pending={setupPending || setupPhoneNumberPending || sendSMSPending}
      type={type}
      allowedTypes={allowedTypes}
      phoneNumber={phoneNumber}
      qrCode={qrCode}
      secret={secret}
      mfaTypeRequired={mfaTypeRequired}
      setupPhoneRequired={setupPhoneRequired}
      sendSmsRequired={sendSmsRequired}
      canBack={canBack}
      initialValues={initialValues}
      onResetMfa={handleResetMfa}
      onCodeExpired={handleCodeExpired}
      onCodeSent={handleCodeSent}
      onSubmit={handleSubmit}
    />
  );
};

export default MfaSetupContainer;
